import { OWNER_STAFF_ID } from './salonDays'
import type { CashDayClose, KassaDay, KassaSummary } from '../types/kassa'

/**
 * Wat een collega in het beheer ziet (docs/KASSA-ABONNEMENTEN.md §4). Eén plek, zodat Vandaag,
 * Agenda en Kassa dezelfde regel volgen. Dit is het scherm; de server dwingt hetzelfde af (403, of
 * stuurt alleen haar eigen rijen mee). Een rij zonder `staffId` (oudere server) hoort bij Sarah.
 */
export type Viewer = { isOwner: boolean; staffId: string }

export const performerOf = (staffId: string | undefined) => staffId || OWNER_STAFF_ID

/** Mag deze kijker deze rij zien? De eigenaar alles; een collega alleen wat zij uitvoert. */
export const isMine = (staffId: string | undefined, viewer: Viewer) => viewer.isOwner || performerOf(staffId) === viewer.staffId

export function ownOnly<T extends { staffId?: string }>(list: readonly T[], viewer: Viewer): T[] {
  return viewer.isOwner ? [...list] : list.filter((x) => isMine(x.staffId, viewer))
}

const EMPTY_SUMMARY: KassaSummary = {
  sales: 0,
  corrected: 0,
  totalCents: 0,
  byMethod: { pin: 0, contant: 0, cadeaubon: 0, tegoed: 0 },
  byKind: { treatments: 0, products: 0, kuren: 0, giftCards: 0 },
  vat: [],
  expectedCashCents: 0,
}

/**
 * De kassadag zoals deze kijker hem ziet. Een collega: alleen haar eigen af te rekenen afspraken
 * en bonnen, geen dagtotalen en geen verwacht kasbedrag (zij telt "blind"; de server rekent het
 * verschil). Bij een afgesloten dag alleen wie, wanneer en wat er geteld is.
 */
export function kassaDayFor(day: KassaDay, viewer: Viewer): KassaDay & { totalsHidden: boolean } {
  if (viewer.isOwner) return { ...day, totalsHidden: false }
  const sales = day.sales.filter((s) => isMine(s.staffId, viewer))
  return {
    ...day,
    sales,
    toSettle: day.toSettle.filter((a) => isMine(a.staffId, viewer)),
    summary: { ...EMPTY_SUMMARY, sales: sales.filter((s) => s.status === 'betaald').length, corrected: sales.filter((s) => s.status === 'gecorrigeerd').length },
    // Terugbetaalde kuren zijn van Sarah (de collega stopt geen kuren en ziet geen bedragen).
    kuurRefunds: [],
    returns: [],
    closed: day.closed ? closeForStaff(day.closed) : null,
    totalsHidden: true,
  }
}

function closeForStaff(c: CashDayClose): CashDayClose {
  return { ...c, expectedCashCents: 0, differenceCents: 0 }
}

/** "Afgesloten door Mila om 17:42" — of zonder naam bij een oudere server. */
export function closedByLabel(c: Pick<CashDayClose, 'closedBy'>): string {
  return c.closedBy?.name ? ` door ${c.closedBy.name}` : ''
}
